import React, { useEffect, useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";

function Header({ cartCount: propCartCount = 0, onSearchClick }) {
  const navigate = useNavigate();
  const location = useLocation();

  // Theme colors
  const accentColor = "rgb(219, 68, 55)";
  const backgroundColor = "#ffffff";
  const textColor = "#000000";
  const textSecondary = "#666666";
  const borderColor = "#e0e0e0";

  // -----------------------------------------------------------------
  // 1. Common data (used by both layouts)
  // -----------------------------------------------------------------
  const dealer_firm_name = localStorage.getItem("dealer_firm_name") || "User";
  const LoginNumber = localStorage.getItem("LoginNumber");
  const notificaion_count = parseInt(
    localStorage.getItem("notificaion_count") || "0",
    10,
  );
  const { pathname } = location;
  const productId = pathname.split("/").pop() || "";

  // Force login redirect
  const protectedPaths = [
    "/MyOrderds",
    "/Mycart",
    "/MyProducts",
    "/Notification",
    "/MyDevice",
    "/OrderDetails",
    "/MyTransaction",
    "/Complain",
    "/UpdateProdile",
    "/UpdateBankDetails",
  ];

  // Check if current path starts with any protected path
  const isProtectedPath = protectedPaths.some((path) =>
    location.pathname.startsWith(path),
  );

  // Force login redirect only for protected paths
  if (isProtectedPath && !LoginNumber?.trim()) {
    const currentPath = location.pathname + location.search;
    localStorage.setItem("login_url", currentPath);
    navigate("/Logout");
  }

  // Cart count state - initialize from localStorage
  const [cartCount, setCartCount] = useState(() => {
    const savedCount = localStorage.getItem("cart_count");
    return savedCount ? parseInt(savedCount) : propCartCount;
  });

  // Listen for cart update events
  useEffect(() => {
    const handleCartUpdate = (event) => {
      if (event.detail && event.detail.cartCount !== undefined) {
        setCartCount(event.detail.cartCount);
      }
    };

    // Listen for custom event
    window.addEventListener("cart-updated", handleCartUpdate);

    // Also listen for storage changes (for cross-tab sync)
    const handleStorageChange = (e) => {
      if (e.key === "cart_count") {
        setCartCount(e.newValue ? parseInt(e.newValue) : 0);
      }
    };
    window.addEventListener("storage", handleStorageChange);

    return () => {
      window.removeEventListener("cart-updated", handleCartUpdate);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  // Also sync from local_cart for backward compatibility
  useEffect(() => {
    const updateFromLocalCart = () => {
      try {
        const cart = localStorage.getItem("local_cart");
        const items = cart ? JSON.parse(cart) : [];
        const count = items.filter((item) => item.product_quantity > 0).length;
        if (count > 0) {
          setCartCount(count);
          localStorage.setItem("cart_count", count.toString());
        }
      } catch (error) {
        console.error("Error reading local_cart:", error);
      }
    };

    updateFromLocalCart();
    window.addEventListener("storage", updateFromLocalCart);
    return () => window.removeEventListener("storage", updateFromLocalCart);
  }, []);

  const displayCartCount = cartCount || propCartCount;

  // Header titles & back-links
  const headers = {
    "/MyOrderds": { title: "Ordered List", backLink: "/" },
    "/OrderDetails": { title: "Ordered Details", backLink: true },
    "/Mycart": { title: "My Cart", backLink: true },
    "/AddProduct": { title: "Product List", backLink: "/" },
    "/ProductDetails": { title: "Product Detail", backLink: "/" },
    "/UpdateProduct": { title: "Update Product", backLink: true },
    "/MyTransaction": { title: "My Transaction", backLink: true },
    "/Complain": { title: "Complain List", backLink: true },
    "/AddComplain": { title: "Add Complain", backLink: true },
    "/UpdateComplain": { title: "Update Complain", backLink: true },
    "/UpdateProdile": { title: "Update Profile", backLink: true },
    "/UpdateProfile": { title: "Update Profile", backLink: true },
    "/UpdateBankDetails": { title: "Bank Detail", backLink: true },
    "/ProductDetails": {
      title: "Product Detail",
      backLink: `/AddProduct#${productId}`,
    },
    "/AllProducts": { title: "All Product", backLink: true },
    "/Notification": { title: "Notification List", backLink: true },
    "/PromotionalImage": { title: "Promotional Preview", backLink: true },
    "/PromotionalComment": { title: "Customer's Experience", backLink: true },
    "/Checkuser": { title: "Update Profile", backLink: true },
    "/MyDevice": { title: "My Device", backLink: true },
  };

  const currentHeader = Object.entries(headers).find(([p]) =>
    pathname.startsWith(p),
  );

  // Search helper
  const handleSearchClick = () => {
    if (pathname.startsWith("/AddProduct") && onSearchClick) {
      onSearchClick();
    } else {
      window.scrollTo({ top: 0, behavior: "smooth" });
      document.querySelector(".search-box input")?.focus();
    }
  };

  // -----------------------------------------------------------------
  // 2. Detect mobile (≤768px) – simple condition
  // -----------------------------------------------------------------
  const isMobile = window.innerWidth <= 768;

  // -----------------------------------------------------------------
  // 3. Render – ONE condition decides the whole UI
  // -----------------------------------------------------------------
  return (
    <>
      {/* ------------------- MOBILE LAYOUT ------------------- */}
      {isMobile ? (
        <div
          className={`header fixed top-0 left-0 w-full z-50`}
          style={{
            backgroundColor:
              pathname.startsWith("/ProductDetails") ||
              pathname.startsWith("/product")
                ? backgroundColor
                : backgroundColor,
            boxShadow: "0 2px 10px rgba(0, 0, 0, 0.08)",
            paddingTop: "env(safe-area-inset-top)",
          }}
        >
          {/* Home page special header */}
          {pathname === "/" && (
            <div
              className="content notch-clear"
              style={{
                margin: "7px 10px 0",
                padding: "0 10px",
                borderRadius: "20px",
                backgroundColor: backgroundColor,
                border: `1px solid ${borderColor}`,
              }}
            >
              <div className="d-flex py-2" style={{ alignItems: "center" }}>
                <div style={{ flex: "1 1 70%", minWidth: "200px" }}>
                  <span
                    className="opacity-80 font-20 Chiller"
                    style={{
                      color: accentColor,
                      fontSize: "clamp(16px, 4vw, 18px)",
                    }}
                  >
                    Welcome's you
                  </span>
                  <h1
                    className="mt-n2 font-27 RobotechGP"
                    style={{
                      fontSize: "clamp(20px,5vw,24px)",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      color: textColor,
                    }}
                  >
                    {dealer_firm_name}
                  </h1>
                </div>
                <div>
                  <Link to="#">
                    <img
                      src="https://aghomedecor.in/AGLOGO.png"
                      alt="Logo"
                      className="img-fluid rounded-circle"
                      style={{
                        width: "clamp(40px,10vw,52px)",
                      }}
                    />
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* Dynamic page header */}
          {currentHeader && (
            <div
              style={{
                display: "flex",
                margin: "clamp(10px,5%,20px)",
                alignItems: "center",
                backgroundColor: pathname.startsWith("/ProductDetails")
                  ? backgroundColor
                  : backgroundColor,
                borderRadius: pathname.startsWith("/ProductDetails")
                  ? "10px"
                  : "0",
                padding: pathname.startsWith("/ProductDetails") ? "8px" : "0",
              }}
            >
              {/* Back button */}
              {currentHeader[1].backLink &&
                (currentHeader[1].backLink === true ? (
                  <button
                    onClick={() => navigate(-1)}
                    style={{
                      background: backgroundColor,
                      border: `1px solid ${accentColor}`,
                      height: "38px",
                      minWidth: "38px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "10px",
                    }}
                  >
                    <i
                      className="fa fa-angle-left"
                      style={{
                        fontSize: "clamp(25px,4vw,20px)",
                        color: accentColor,
                      }}
                    />
                  </button>
                ) : (
                  <Link
                    to={currentHeader[1].backLink}
                    style={{
                      background: backgroundColor,
                      border: `1px solid ${accentColor}`,
                      height: "38px",
                      minWidth: "38px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "10px",
                    }}
                  >
                    <i
                      className="fa fa-angle-left"
                      style={{
                        fontSize: "clamp(16px,4vw,20px)",
                        color: accentColor,
                      }}
                    />
                  </Link>
                ))}

              {/* Title + optional Bank link */}
              <h1
                style={{
                  marginLeft:
                    pathname.startsWith("/AddProduct") ||
                    pathname.startsWith("/ProductDetails")
                      ? "clamp(50px,10vw,50px)"
                      : "clamp(10px,3vw,15px)",
                  fontSize: "clamp(18px,5vw,22px)",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  color: textColor,
                  flex: 1,
                }}
              >
                {currentHeader[1].title}
                {pathname === "/MyOrderds" && (
                  <Link
                    to="/UpdateBankDetails"
                    style={{
                      float: "right",
                      fontSize: "clamp(12px,3vw,15px)",
                      background: accentColor,
                      border: `1px solid ${accentColor}`,
                      borderRadius: "10px",
                      padding: "clamp(3px,1vw,5px)",
                      marginRight: "0%",
                      color: "#ffffff",
                      textDecoration: "none",
                    }}
                  >
                    Bank Detail
                  </Link>
                )}
              </h1>
            </div>
          )}
        </div>
      ) : (
        /* ------------------- WEB (DESKTOP) LAYOUT ------------------- */
        <>
          <style jsx>{`
            .fa {
              color: ${textColor} !important;
            }
            .sticky-header {
              position: sticky;
              top: 0;
              width: 100%;
              z-index: 1000;
              background: ${backgroundColor};
              box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
              padding: 8px 0;
              border-bottom: 1px solid ${borderColor};
            }
            .header-content {
              display: flex;
              align-items: center;
              justify-content: space-between;
              padding: 0 clamp(8px, 2vw, 16px);
            }
            .logo-img {
              width: clamp(45px, 10vw, 60px);
              height: clamp(45px, 10vw, 60px);
              border-radius: 50%;
              object-fit: cover;
            }
            .back-btn {
              background: ${backgroundColor};
              border: 1px solid ${accentColor};
              width: clamp(34px, 9vw, 40px);
              height: clamp(34px, 9vw, 40px);
              border-radius: 10px;
              display: flex;
              align-items: center;
              justify-content: center;
              color: ${accentColor};
              font-size: clamp(18px, 5vw, 22px);
              text-decoration: none;
              cursor: pointer;
            }
            .page-title {
              color: ${textColor};
              font-size: clamp(16px, 4.5vw, 22px);
              margin: 0 0 0 8px;
              flex: 1;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
              font-weight: 600;
            }
            .bank-link {
              background: ${accentColor};
              border: 1px solid ${accentColor};
              color: #ffffff;
              padding: 4px 8px;
              border-radius: 8px;
              font-size: clamp(11px, 3vw, 14px);
              text-decoration: none;
              margin-left: 8px;
              transition: all 0.3s ease;
            }
            .bank-link:hover {
              background: #d63224;
              transform: translateY(-1px);
            }
            .nav-icons {
              display: flex;
              gap: clamp(6px, 1.5vw, 12px);
              align-items: center;
            }
            .nav-icon {
              position: relative;
              width: clamp(36px, 9vw, 44px);
              height: clamp(36px, 9vw, 44px);
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              color: ${textColor};
              font-size: clamp(16px, 4.5vw, 20px);
              text-decoration: none;
              transition: all 0.3s ease;
              background: ${backgroundColor};
              border: 1px solid ${borderColor};
            }
            .nav-icon:hover {
              background: rgba(219, 68, 55, 0.1);
              border-color: ${accentColor};
              transform: translateY(-2px);
              box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
            }
            .nav-icon.active {
              background: ${accentColor};
              color: #ffffff !important;
              border-color: ${accentColor};
            }
            .nav-icon.active i {
              color: #ffffff !important;
            }
            .nav-icon img {
              width: clamp(18px, 5vw, 22px);
              height: clamp(18px, 5vw, 22px);
            }
            .nav-icon.active img {
              filter: brightness(0) invert(1);
            }
            .badge {
              position: absolute;
              top: -5px;
              right: -5px;
              background: ${accentColor};
              color: #ffffff;
              font-size: 10px;
              font-weight: bold;
              width: 18px;
              height: 18px;
              border-radius: 50%;
              display: flex;
              align-items: center;
              justify-content: center;
              border: 2px solid ${backgroundColor};
            }
            @media (min-width: 769px) {
              .nav-icons {
                gap: 12px;
              }
              .nav-icon {
                width: 44px;
                height: 44px;
                font-size: 20px;
              }
              .nav-icon img {
                width: 22px;
                height: 22px;
              }
              .logo-img {
                width: 60px;
                height: 60px;
              }
            }
          `}</style>

          <header className="sticky-header">
            <div className="header-content">
              {/* LEFT – Logo / Back + Title */}
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  flex: 1,
                  minWidth: 0,
                }}
              >
                {currentHeader ? (
                  <>
                    {currentHeader[1].backLink &&
                      (currentHeader[1].backLink === true ? (
                        <button
                          onClick={() => navigate(-1)}
                          className="back-btn"
                        >
                          <i className="fa fa-angle-left" />
                        </button>
                      ) : (
                        <Link
                          to={currentHeader[1].backLink}
                          className="back-btn"
                        >
                          <i className="fa fa-angle-left" />
                        </Link>
                      ))}
                    <h1 className="page-title">
                      {currentHeader[1].title}
                      {pathname === "/MyOrderds" && (
                        <Link
                          to="/UpdateBankDetails"
                          className="bank-link"
                          style={{ marginLeft: "10px" }}
                        >
                          Bank Detail
                        </Link>
                      )}
                    </h1>
                  </>
                ) : (
                  <>
                    <Link to="/">
                      <img
                        src="https://aghomedecor.in/AGLOGO.png"
                        alt="Logo"
                        className="logo-img"
                      />
                    </Link>
                    {pathname === "/MyOrderds" && (
                      <Link
                        to="/UpdateBankDetails"
                        className="bank-link"
                        style={{ marginLeft: "10px" }}
                      >
                        Bank Detail
                      </Link>
                    )}
                  </>
                )}
              </div>

              {/* RIGHT – Icons */}
              <nav className="nav-icons">
                {pathname.startsWith("/AddProduct") ? (
                  <>
                    {/* <Link to="/scan" className="nav-icon">
                      <img src="./ScanQR.png" alt="Scan" />
                    </Link> */}
                    <Link
                      to="/Mycart"
                      className={
                        pathname === "/Mycart" ? "nav-icon active" : "nav-icon"
                      }
                      style={{ position: "relative" }}
                    >
                      <i className="fa fa-shopping-cart" />
                      {displayCartCount > 0 && (
                        <span className="badge">{displayCartCount}</span>
                      )}
                    </Link>
                  </>
                ) : (
                  <>
                    <Link
                      to="/"
                      className={
                        pathname === "/" ? "nav-icon active" : "nav-icon"
                      }
                    >
                      <i className="fa fa-home" />
                    </Link>

                    <Link
                      to="/AddProduct"
                      onClick={handleSearchClick}
                      className={
                        pathname.startsWith("/AddProduct")
                          ? "nav-icon active"
                          : "nav-icon"
                      }
                    >
                      {pathname.startsWith("/AddProduct") ? (
                        <i className="fa fa-search" />
                      ) : (
                        <i className="fa fa-archive" />
                      )}
                    </Link>

                    <Link
                      to="/MyOrderds"
                      className={
                        pathname === "/MyOrderds"
                          ? "nav-icon active"
                          : "nav-icon"
                      }
                    >
                      <i className="fa fa-cubes" />
                    </Link>

                    <Link
                      to="/Mycart"
                      className={
                        pathname === "/Mycart" ? "nav-icon active" : "nav-icon"
                      }
                      style={{ position: "relative" }}
                    >
                      <i className="fa fa-shopping-cart" />
                      {displayCartCount > 0 && (
                        <span className="badge">{displayCartCount}</span>
                      )}
                    </Link>

                    <Link
                      to="/More"
                      className={
                        pathname === "/More" ? "nav-icon active" : "nav-icon"
                      }
                    >
                      <i className="fa fa-list" />
                    </Link>
                  </>
                )}
              </nav>
            </div>
          </header>
        </>
      )}
    </>
  );
}

export default Header;
